import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

export const STUDIO_BACKGROUND = '#e4e4e4';

export const CAMERA_HOME = {
  position: new THREE.Vector3(0.15, 1.42, 8.15),
  fov: 28,
};

export const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return reduced;
};

/** Soft-box style environment so glass and the cap pick up studio reflections. */
export const StudioEnvironment: React.FC = () => {
  const gl = useThree((state) => state.gl);
  const scene = useThree((state) => state.scene);

  useLayoutEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    const envScene = new THREE.Scene();
    // Dark fill + thin bright cards: glass needs contrast to catch a rim and a highlight.
    envScene.background = new THREE.Color('#c9ced4');

    const addPanel = (
      color: string,
      width: number,
      height: number,
      position: [number, number, number],
    ) => {
      const panel = new THREE.Mesh(
        new THREE.PlaneGeometry(width, height),
        new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }),
      );
      panel.position.set(...position);
      panel.lookAt(0, 1.1, 0);
      envScene.add(panel);
    };

    addPanel('#ffffff', 0.32, 6.8, [2.45, 2.6, 4.2]);
    addPanel('#ffffff', 6.2, 0.28, [0.1, 5.7, 2.2]);
    addPanel('#1a1c20', 3.2, 6.4, [-4.8, 2.3, 2.1]);
    addPanel('#d5d8de', 12, 8, [0, 2, -5]);
    addPanel('#ffffff', 0.9, 1.1, [-2.2, 4.5, 4.3]);

    const environment = pmrem.fromScene(envScene, 0.02).texture;
    scene.environment = environment;

    return () => {
      scene.environment = null;
      environment.dispose();
      pmrem.dispose();
      envScene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          const { material } = object;
          if (Array.isArray(material)) material.forEach((entry) => entry.dispose());
          else material.dispose();
        }
      });
    };
  }, [gl, scene]);

  return null;
};

export const StudioLights: React.FC = () => (
  <>
    <hemisphereLight args={['#eef1f4', '#b8b8b8', 0.18]} />
    <directionalLight
      castShadow
      position={[4.6, 7.4, -1.4]}
      intensity={1.15}
      color="#ffffff"
      shadow-mapSize={[2048, 2048]}
      shadow-radius={14}
      shadow-blurSamples={24}
      shadow-bias={-0.0005}
      shadow-camera-near={1}
      shadow-camera-far={24}
      shadow-camera-left={-7}
      shadow-camera-right={7}
      shadow-camera-top={7}
      shadow-camera-bottom={-7}
    />
    <directionalLight position={[-5, 2.2, 3]} intensity={0.22} color="#dfe4ea" />
    <directionalLight position={[2.8, 3.4, 5.6]} intensity={2.4} color="#ffffff" />
    <directionalLight position={[-2.6, 4.6, 5.4]} intensity={1.6} color="#ffffff" />
  </>
);

/** Seamless studio sweep: flat floor curving up into the backdrop. */
export const Sweep: React.FC = () => {
  const geometry = useMemo(() => {
    const samples = 64;
    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const halfWidth = 24;

    for (let index = 0; index <= samples; index += 1) {
      const t = index / samples;
      const z = 12 - t * 24;
      const curve = Math.max(0, -1.15 - z);
      const y = curve * curve * 0.42;
      const shade = 0.84 - Math.min(y / 6, 1) * 0.06;
      positions.push(-halfWidth, y, z, halfWidth, y, z);
      colors.push(shade, shade, shade, shade, shade, shade);

      if (index < samples) {
        const vertex = index * 2;
        indices.push(vertex, vertex + 1, vertex + 2, vertex + 1, vertex + 3, vertex + 2);
      }
    }

    const sweep = new THREE.BufferGeometry();
    sweep.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    sweep.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    sweep.setIndex(indices);
    sweep.computeVertexNormals();
    return sweep;
  }, []);

  return (
    <>
      <mesh geometry={geometry}>
        <meshBasicMaterial vertexColors side={THREE.DoubleSide} />
      </mesh>
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.002, 0]}>
        <planeGeometry args={[40, 40]} />
        <shadowMaterial transparent opacity={0.26} color="#000000" />
      </mesh>
    </>
  );
};

export const configureRenderer = (gl: THREE.WebGLRenderer) => {
  gl.toneMapping = THREE.ACESFilmicToneMapping;
  gl.toneMappingExposure = 1;
  gl.shadowMap.type = THREE.VSMShadowMap;
};
